import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import { Bike, Store, Minus, Plus, Loader2, Droplets } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES, TIME_SLOTS } from "@/data/services";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function BookingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const initialService = SERVICES.some((s) => s.id === params.get("service"))
    ? params.get("service")
    : "deep";

  const [service, setService] = useState(initialService);
  const [pairs, setPairs] = useState(1);
  const [fulfillment, setFulfillment] = useState("pickup");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [date, setDate] = useState(format(addDays(new Date(), 1), "yyyy-MM-dd"));
  const [timeSlot, setTimeSlot] = useState(TIME_SLOTS[0]);
  const [notes, setNotes] = useState("");
  const [payConfig, setPayConfig] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api
      .get("/payments/config")
      .then(({ data }) => setPayConfig(data))
      .catch(() => setPayConfig({ client_id: null }));
  }, []);

  const selected = useMemo(() => SERVICES.find((s) => s.id === service), [service]);
  const total = selected.price * pairs;

  if (user === null) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian">
        <Loader2 className="animate-spin text-aqua" />
      </main>
    );
  }
  if (user === false) {
    return <Navigate to="/auth" state={{ from: "/book" }} replace />;
  }

  const bookingPayload = {
    service,
    pairs,
    fulfillment,
    address,
    postcode,
    preferred_date: date,
    time_slot: timeSlot,
    notes,
  };

  const continueToPayment = (e) => {
    e.preventDefault();
    if (address.trim().length < 5) return toast.error("Please enter a valid address");
    if (postcode.trim().length < 3) return toast.error("Please enter a valid postcode");
    if (!date) return toast.error("Please choose a date");
    setPaymentReady(true);
    setTimeout(
      () => document.querySelector('[data-testid="booking-summary"]')?.scrollIntoView({ behavior: "smooth", block: "center" }),
      100
    );
  };

  const payWithStripe = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/stripe/checkout", {
        ...bookingPayload,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment");
      setPaying(false);
    }
  };

  const createOrder = async () => {
    const { data } = await api.post("/payments/paypal/create-order", bookingPayload);
    return data.order_id;
  };

  const onApprove = async (d) => {
    setPaying(true);
    try {
      const { data } = await api.post(`/payments/paypal/capture-order/${d.orderID}`);
      toast.success(`Paid — booking ${data.ref} confirmed. Confirmation sent to your inbox.`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Payment could not be completed");
      setPaymentReady(false);
    } finally {
      setPaying(false);
    }
  };

  return (
    <main className="min-h-screen bg-obsidian px-6 py-16 lg:px-12" data-testid="booking-page">
      <div className="mx-auto max-w-6xl">
        <Link to="/" data-testid="booking-logo" className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center border border-aqua/50 text-aqua">
            <Droplets size={18} />
          </span>
          <span className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white">
            Clean <span className="text-aqua">&</span> Revive
          </span>
        </Link>

        <p className="mt-14 font-mono text-xs uppercase tracking-[0.25em] text-aqua">Book a clean</p>
        <h1 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tighter text-white sm:text-5xl">
          Your trainers,<br />our table.
        </h1>

        <form onSubmit={continueToPayment} className="mt-14 grid gap-12 lg:grid-cols-3">
          <div
            className={`space-y-12 transition-opacity duration-300 lg:col-span-2 ${
              paymentReady ? "pointer-events-none opacity-50" : ""
            }`}
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">01 — Service</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                {SERVICES.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    data-testid={`booking-service-${s.id}`}
                    onClick={() => setService(s.id)}
                    className={`border p-5 text-left transition-colors duration-300 ${
                      service === s.id ? "border-aqua bg-aqua/5" : "border-white/10 bg-carbon hover:border-white/30"
                    }`}
                  >
                    <p className="font-display text-base font-bold uppercase tracking-tight text-white">{s.name}</p>
                    <p className="mt-2 font-display text-2xl font-extrabold text-aqua">£{s.price}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">per pair</p>
                  </button>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Pairs</p>
                <div className="flex items-center border border-white/10">
                  <button
                    type="button"
                    data-testid="booking-pairs-minus"
                    onClick={() => setPairs((p) => Math.max(1, p - 1))}
                    className="px-4 py-2 text-zinc-300 transition-colors hover:text-aqua"
                  >
                    <Minus size={14} />
                  </button>
                  <span data-testid="booking-pairs-count" className="w-10 text-center font-display text-lg font-bold text-white">
                    {pairs}
                  </span>
                  <button
                    type="button"
                    data-testid="booking-pairs-plus"
                    onClick={() => setPairs((p) => Math.min(10, p + 1))}
                    className="px-4 py-2 text-zinc-300 transition-colors hover:text-aqua"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">02 — Handover</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {[
                  { id: "pickup", icon: Bike, title: "Doorstep collection", desc: "We collect from your address — free locally." },
                  { id: "dropoff", icon: Store, title: "Local drop-off", desc: "Leave your pair at a partner drop-off point." },
                ].map((f) => (
                  <button
                    type="button"
                    key={f.id}
                    data-testid={`booking-fulfillment-${f.id}`}
                    onClick={() => setFulfillment(f.id)}
                    className={`flex items-start gap-4 border p-5 text-left transition-colors duration-300 ${
                      fulfillment === f.id ? "border-aqua bg-aqua/5" : "border-white/10 bg-carbon hover:border-white/30"
                    }`}
                  >
                    <f.icon size={20} className={fulfillment === f.id ? "text-aqua" : "text-zinc-500"} />
                    <span>
                      <span className="block font-display text-sm font-bold uppercase tracking-tight text-white">{f.title}</span>
                      <span className="mt-1 block text-xs text-zinc-400">{f.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">03 — Details</p>
              <div className="mt-5 grid gap-6 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Address</Label>
                  <Input
                    data-testid="booking-address-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                    minLength={5}
                    placeholder="Street, city"
                    className="border-white/15 bg-carbon text-white focus-visible:ring-aqua"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Postcode</Label>
                  <Input
                    data-testid="booking-postcode-input"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    required
                    minLength={3}
                    placeholder="E1 6AN"
                    className="border-white/15 bg-carbon text-white focus-visible:ring-aqua"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Preferred date</Label>
                  <Input
                    type="date"
                    data-testid="booking-date-input"
                    value={date}
                    min={format(new Date(), "yyyy-MM-dd")}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    className="border-white/15 bg-carbon text-white [color-scheme:dark] focus-visible:ring-aqua"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Time slot</Label>
                  <div className="flex flex-wrap gap-3">
                    {TIME_SLOTS.map((slot) => (
                      <button
                        type="button"
                        key={slot}
                        data-testid={`booking-slot-${slot.slice(0, 2)}`}
                        onClick={() => setTimeSlot(slot)}
                        className={`border px-4 py-2.5 font-mono text-xs tracking-[0.1em] transition-colors duration-300 ${
                          timeSlot === slot ? "border-aqua text-aqua" : "border-white/10 text-zinc-400 hover:border-white/30"
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                    Notes (optional)
                  </Label>
                  <Textarea
                    data-testid="booking-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Brand, colourway, stains we should know about..."
                    className="min-h-[90px] border-white/15 bg-carbon text-white focus-visible:ring-aqua"
                  />
                </div>
              </div>
            </div>
          </div>

          <aside className="h-fit border border-white/10 bg-carbon p-8 lg:sticky lg:top-24" data-testid="booking-summary">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Summary</p>
            <div className="mt-6 space-y-4 text-sm">
              <div className="flex justify-between text-zinc-300">
                <span>{selected.name}</span>
                <span>£{selected.price}</span>
              </div>
              <div className="flex justify-between text-zinc-300">
                <span>Pairs</span>
                <span>x {pairs}</span>
              </div>
              <div className="flex justify-between text-zinc-300">
                <span>{fulfillment === "pickup" ? "Collection" : "Drop-off"}</span>
                <span className="text-aqua">Free</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>{date}</span>
                <span>{timeSlot}</span>
              </div>
            </div>
            <div className="mt-6 flex items-end justify-between border-t border-white/10 pt-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Total</span>
              <span data-testid="booking-total" className="font-display text-4xl font-extrabold tracking-tighter text-white">
                £{total}
              </span>
            </div>
            {!paymentReady ? (
              <>
                <button
                  type="submit"
                  data-testid="booking-submit-button"
                  className="mt-8 flex w-full items-center justify-center gap-2 border border-aqua bg-aqua px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-aqua"
                >
                  Continue to payment
                </button>
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600">
                  Secure online payment by PayPal
                </p>
              </>
            ) : (
              <div className="mt-8" data-testid="booking-paypal-section">
                <button
                  type="button"
                  data-testid="booking-edit-details-button"
                  onClick={() => setPaymentReady(false)}
                  className="mb-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-aqua"
                >
                  Edit details
                </button>
                {paying && (
                  <p className="mb-4 flex items-center gap-2 font-mono text-xs text-aqua">
                    <Loader2 size={14} className="animate-spin" /> Confirming payment...
                  </p>
                )}
                <button
                  type="button"
                  data-testid="booking-stripe-button"
                  onClick={payWithStripe}
                  disabled={paying}
                  className="flex w-full items-center justify-center gap-2 border border-aqua bg-aqua px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-aqua disabled:opacity-50"
                >
                  {paying && <Loader2 size={14} className="animate-spin" />}
                  Pay £{total} securely
                </button>
                <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600">
                  Card, Apple Pay & Google Pay — powered by Stripe
                </p>
                {payConfig?.client_id && (
                  <>
                    <div className="my-6 flex items-center gap-4">
                      <span className="h-px flex-1 bg-white/10" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">or</span>
                      <span className="h-px flex-1 bg-white/10" />
                    </div>
                    <PayPalScriptProvider options={{ clientId: payConfig.client_id, currency: "GBP" }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "black", shape: "rect", label: "pay" }}
                        forceReRender={[total]}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={() => {
                          toast.error("Payment could not be completed");
                          setPaymentReady(false);
                        }}
                      />
                    </PayPalScriptProvider>
                  </>
                )}
              </div>
            )}
          </aside>
        </form>
      </div>
    </main>
  );
}
